// --- START OF FILE AuthContext.jsx (Updated for OTP Login) ---

import React, { createContext, useContext, useState, useEffect } from 'react';
// 1. IMPORT CHANGE: Import signInWithOtp and remove signInWithEmailPassword (if not used elsewhere)
import {
  supabase,
  getCurrentUser,
  signInWithOtp,
  verifyOtp,
  signUpWithEmailPassword,
  signOut
} from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    console.warn('useAuth must be used within an AuthProvider. Falling back to default guest state.');
    return {
      user: null,
      role: null,
      loading: true,
      session: null,
      login: async () => ({ success: false, error: 'Auth not initialized' }),
      verifyLoginOtp: async () => ({ success: false, error: 'Auth not initialized' }),
      logout: async () => ({ success: false, error: 'Auth not initialized' }),
      signUp: async () => ({ success: false, error: 'Auth not initialized' }),
      isAuthenticated: false,
    };
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [role, setRole] = useState(null);

  useEffect(() => {
    const fetchAndSetUser = async (session) => {
  if (!session?.user) {
    setUser(null);
    setRole(null);
    setSession(null);
    return;
  }

  try {
    const { data: profile } = await supabase
      .from("profiles")
      .select("full_name, role, email")
      .eq("id", session.user.id)
      .maybeSingle();

    // Check provider table
    const { data: provider } = await supabase
      .from("providers")
      .select("*")
      .eq("user_id", session.user.id)
      .eq("is_active", true)
      .maybeSingle();

    if (provider) {
      setUser({
        ...session.user,
        full_name: provider.full_name,
        email: provider.email,
      });

      setRole("provider");
      return;
    }

    setUser({
      ...session.user,
      ...(profile || {}),
    });

    setRole(profile?.role || "user");

  } catch (err) {
    console.error(err);

    setUser({
      ...session.user,
    });

    setRole(null);
  }
};

    // Fetch initial session
    getCurrentUser().then(({ data: { session } }) => {
      setSession(session);
      fetchAndSetUser(session).finally(() => {
        setLoading(false);
      });
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      fetchAndSetUser(session);
      setLoading(false);
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // 2. LOGIN FUNCTION UPDATE: Changed to use signInWithOtp (Magic Link/Passwordless)
  const login = async (email) => {
    try {
      const { data, error } = await signInWithOtp(email);

      if (error) throw error;

      return {
        success: true,
        message: 'OTP sent successfully',
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  };

  const verifyLoginOtp = async (email, otp) => {
    try {
      const { data, error } = await verifyOtp(email, otp);

      if (error) throw error;

      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
      };
    }
  };

  // 3. SIGN UP FUNCTION: Remains the same (still using password for registration)
  const signUp = async (email, password, metadata) => {
    try {
      const { data, error } = await signUpWithEmailPassword(email, password);
      if (error) throw error;

      const { error: profileError } = await supabase.from('profiles').insert([
        {
          id: data.user.id,
          full_name: metadata.full_name,
          role: metadata.role,
          email: email,
        },
      ]);

      if (profileError) throw profileError;

      return { success: true, data };
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: error.message };
    }
  };

  // 4. LOGOUT FUNCTION: Remains the same
  const logout = async () => {
    try {
      await signOut();
      setUser(null);
      setSession(null);
      setRole(null);
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    role,
    loading,
    session,
    login,
    verifyLoginOtp,
    logout,
    signUp,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
// --- END OF FILE AuthContext.jsx ---
